/**
 * Recent searches (last 10) and recently opened results of the search box (T11-07), kept in this
 * browser per staff member. Only what staff typed / opened is stored — titles of products and
 * order numbers, never customer e-mail addresses or phone numbers (customer and consignor results
 * are remembered by their name only).
 */
import { z } from "zod";

export const HISTORY_LIMIT = 10;

export interface RecentResult {
  /** Where it opens. */
  to: string;
  title: string;
  kind: "product" | "order" | "customer" | "consignor" | "page" | "action";
}

const recentSchema = z.object({
  to: z.string().startsWith("/"),
  title: z.string().max(200),
  kind: z.enum(["product", "order", "customer", "consignor", "page", "action"]),
});

const queriesKey = (staffId: string) => `tcg.admin.search.queries:${staffId}`;
const resultsKey = (staffId: string) => `tcg.admin.search.opened:${staffId}`;

function storageOf(storage?: Storage): Storage | undefined {
  if (storage) {
    return storage;
  }
  return typeof window === "undefined" ? undefined : window.localStorage;
}

function read(storage: Storage | undefined, key: string): unknown {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function write(storage: Storage | undefined, key: string, value: unknown) {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // private mode / quota — history is a convenience only
  }
}

export function loadQueries(staffId: string, storage?: Storage): string[] {
  const parsed = z
    .array(z.string().max(200))
    .safeParse(read(storageOf(storage), queriesKey(staffId)));
  return parsed.success ? parsed.data.slice(0, HISTORY_LIMIT) : [];
}

export function rememberQuery(staffId: string, query: string, storage?: Storage): string[] {
  const term = query.trim();
  if (term.length < 2) {
    return loadQueries(staffId, storage);
  }
  const next = [term, ...loadQueries(staffId, storage).filter((q) => q !== term)].slice(
    0,
    HISTORY_LIMIT,
  );
  write(storageOf(storage), queriesKey(staffId), next);
  return next;
}

export function loadOpened(staffId: string, storage?: Storage): RecentResult[] {
  const parsed = z.array(recentSchema).safeParse(read(storageOf(storage), resultsKey(staffId)));
  return parsed.success ? parsed.data.slice(0, HISTORY_LIMIT) : [];
}

export function rememberOpened(
  staffId: string,
  result: RecentResult,
  storage?: Storage,
): RecentResult[] {
  const next = [result, ...loadOpened(staffId, storage).filter((r) => r.to !== result.to)].slice(
    0,
    HISTORY_LIMIT,
  );
  write(storageOf(storage), resultsKey(staffId), next);
  return next;
}

export function clearHistory(staffId: string, storage?: Storage): void {
  const target = storageOf(storage);
  target?.removeItem(queriesKey(staffId));
  target?.removeItem(resultsKey(staffId));
}
